// QaScorecard — per-engineer card: stat grid, focus mix, strength/growth, and the
// AI coach buttons. Ported from qa-live (QA-2286). The html2canvas export is
// replaced with the SVG-serialize technique this app already uses in
// ExportableSection (no extra dependency), and a Copy-as-markdown action.

import { useRef, useState } from 'react'
import { getInitials } from './pulseHelpers.js'
import { colorFromName } from './pulseHelpers.js'
import { AVATAR_PALETTE, PULSE_COLORS } from './pulseConfig.js'
import { AREA_BY_KEY, FOCUS_AREAS, focusMix } from './focusMix.js'
import QaCoachButtons from './QaCoachButtons.jsx'

function buildMarkdown(m) {
    const mix = focusMix(m)
    const total = Object.values(mix).reduce((s, v) => s + v, 0)
    const mixLine =
        total > 0
            ? FOCUS_AREAS.map((a) => ({ a, v: mix[a.key] }))
                  .filter((x) => x.v > 0)
                  .sort((x, y) => y.v - x.v)
                  .map((x) => `${x.a.label} ${Math.round((x.v / total) * 100)}%`)
                  .join(', ')
            : 'No measurable activity'
    const ranked = FOCUS_AREAS.map((a) => ({ a, v: mix[a.key] })).sort((x, y) => y.v - x.v)
    const strength = ranked[0]?.a.label
    const growth = ranked[ranked.length - 1]?.a.label

    return [
        `**${m.name} — QA Impact (last 12 months)**`,
        `Impact score: ${m.score}`,
        '',
        `- 🐛 Bugs found: ${m.bugsCreated}`,
        `- ✅ Bugs closed: ${m.bugsClosed}`,
        `- 📋 QA stories closed: ${m.qaStoriesClosed}`,
        `- 🤖 Stories automated: ${m.storiesAutomated}`,
        `- ✨ AI tests authored: ${m.aiCompletedTests}`,
        `- 🦁 MRs merged: ${m.mergeRequests}`,
        `- ⚙️ Commits: ${m.totalCommits}`,
        '',
        `Focus mix: ${mixLine}`,
        `Strength: ${strength}${total > 0 && growth !== strength ? ` · Growth area: ${growth}` : ''}`,
        (m.badges ?? []).length ? `Badges: ${m.badges.map((b) => b.badge).join(', ')}` : '',
    ]
        .filter(Boolean)
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
}

// Export a node to PNG via SVG foreignObject — the same dependency-free technique
// used by ExportableSection.
async function exportNodeToPng(node, filename) {
    const rect = node.getBoundingClientRect()
    const width = Math.ceil(rect.width)
    const height = Math.ceil(rect.height)
    if (!width || !height) return
    const clone = node.cloneNode(true)
    clone.querySelectorAll('[data-export-ignore="true"]').forEach((el) => el.remove())
    clone.style.width = `${width}px`
    clone.style.minHeight = `${height}px`
    clone.style.boxSizing = 'border-box'
    const serialized = new XMLSerializer().serializeToString(clone)
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml" style="background:${PULSE_COLORS.bgInner};">${serialized}</div></foreignObject></svg>`
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }))
    try {
        await new Promise((resolve, reject) => {
            const image = new Image()
            image.onload = () => {
                const canvas = document.createElement('canvas')
                canvas.width = width * 2
                canvas.height = height * 2
                const ctx = canvas.getContext('2d')
                ctx.scale(2, 2)
                ctx.drawImage(image, 0, 0, width, height)
                const link = document.createElement('a')
                link.href = canvas.toDataURL('image/png')
                link.download = filename
                link.click()
                resolve()
            }
            image.onerror = reject
            image.src = url
        })
    } finally {
        URL.revokeObjectURL(url)
    }
}

export default function QaScorecard({ member: m, idx, team }) {
    const cardRef = useRef(null)
    const [copied, setCopied] = useState(false)
    const [exporting, setExporting] = useState(false)

    const avatar = colorFromName(m.name, AVATAR_PALETTE)
    const mix = focusMix(m)
    const ranked = FOCUS_AREAS.map((a) => ({ ...a, value: mix[a.key] })).sort((a, b) => b.value - a.value)
    const total = ranked.reduce((s, a) => s + a.value, 0)
    const strength = ranked[0]
    const growth = ranked[ranked.length - 1]

    async function copyMarkdown() {
        await navigator.clipboard.writeText(buildMarkdown(m))
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1400)
    }

    async function exportPng() {
        if (!cardRef.current) return
        setExporting(true)
        try {
            await exportNodeToPng(
                cardRef.current,
                `qa-scorecard-${m.name.split(' ')[0].toLowerCase()}-${new Date().toISOString().slice(0, 10)}.png`
            )
        } finally {
            setExporting(false)
        }
    }

    const iconBtn = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        borderRadius: '6px',
        border: `1px solid ${PULSE_COLORS.border}`,
        background: PULSE_COLORS.cardBg,
        padding: '2px 6px',
        fontSize: '11px',
        fontWeight: 600,
        color: PULSE_COLORS.textMuted,
        cursor: 'pointer',
    }

    const stats = [
        {
            emoji: '🐛',
            value: m.bugsCreated,
            label: 'found',
            tip: 'Bugs found — defects this engineer filed (reporter)',
        },
        {
            emoji: '✅',
            value: m.bugsClosed,
            label: 'closed',
            tip: 'Bugs closed — defects this engineer resolved (assignee)',
        },
        {
            emoji: '📋',
            value: m.qaStoriesClosed,
            label: 'QA',
            tip: 'QA stories closed — QA-owned stories moved to Done/Closed',
        },
        {
            emoji: '🤖',
            value: m.storiesAutomated,
            label: 'auto',
            tip: 'Stories automated — issues this engineer flipped to Automation Status = Automated',
        },
        {
            emoji: '✨',
            value: m.aiCompletedTests,
            label: 'AI',
            tip: '@aiCompleted tests authored in the e2e repo (by GitLab blame)',
        },
        { emoji: '🦁', value: m.mergeRequests, label: 'MRs', tip: 'Merged merge requests across tracked QA repos' },
    ]

    return (
        <div
            ref={cardRef}
            style={{
                borderRadius: '8px',
                border: `1px solid ${PULSE_COLORS.border}`,
                background: PULSE_COLORS.bgInner,
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span
                    style={{
                        width: '20px',
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: PULSE_COLORS.textSubtle,
                    }}
                >
                    {idx + 1}
                </span>
                <span
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 700,
                        flexShrink: 0,
                        backgroundColor: avatar,
                    }}
                >
                    {getInitials(m.name)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: PULSE_COLORS.text,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {m.name}
                    </div>
                    <div style={{ fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                        <span title="Composite quality score — weighted blend of bugs, QA stories, automation, AI tests, MRs, and commits. Same scale as the Leaderboard.">
                            {m.score} impact pts
                        </span>
                        {(m.badges ?? []).length > 0 && (
                            <span style={{ marginLeft: '4px' }}>
                                {m.badges.map((b) => (
                                    <span
                                        key={b.badge}
                                        title={`${b.badge} — ${b.description} (${b.metric} ${b.metricLabel})`}
                                    >
                                        {b.emoji}{' '}
                                    </span>
                                ))}
                            </span>
                        )}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} data-export-ignore="true">
                    <button
                        type="button"
                        onClick={copyMarkdown}
                        title="Copy scorecard text (for a review doc or message)"
                        style={iconBtn}
                    >
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                    <button
                        type="button"
                        onClick={exportPng}
                        disabled={exporting}
                        title="Download scorecard as an image"
                        style={{ ...iconBtn, opacity: exporting ? 0.5 : 1 }}
                    >
                        {exporting ? '…' : '🖼️'}
                    </button>
                </div>
            </div>

            <div
                style={{
                    marginTop: '12px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '6px',
                    textAlign: 'center',
                }}
            >
                {stats.map((s) => (
                    <div
                        key={s.label}
                        title={s.tip}
                        style={{
                            borderRadius: '6px',
                            background: PULSE_COLORS.cardBg,
                            border: `1px solid ${PULSE_COLORS.border}`,
                            padding: '4px 0',
                            cursor: 'help',
                        }}
                    >
                        <div
                            style={{
                                fontSize: '14px',
                                fontWeight: 700,
                                color: PULSE_COLORS.text,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {s.value}
                        </div>
                        <div style={{ fontSize: '10px', color: PULSE_COLORS.textSubtle }}>
                            {s.emoji} {s.label}
                        </div>
                    </div>
                ))}
            </div>

            <div style={{ marginTop: '12px' }}>
                <div
                    style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: PULSE_COLORS.textSubtle,
                        marginBottom: '4px',
                    }}
                >
                    Focus mix
                </div>
                <div
                    style={{
                        display: 'flex',
                        height: '10px',
                        borderRadius: '9999px',
                        overflow: 'hidden',
                        background: PULSE_COLORS.border,
                    }}
                >
                    {total > 0 ? (
                        ranked
                            .filter((a) => a.value > 0)
                            .map((a) => (
                                <div
                                    key={a.key}
                                    title={`${a.label} ${Math.round((a.value / total) * 100)}% — ${a.desc}`}
                                    style={{ width: `${(a.value / total) * 100}%`, backgroundColor: a.color }}
                                />
                            ))
                    ) : (
                        <div style={{ width: '100%', background: PULSE_COLORS.border }} />
                    )}
                </div>
                <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                    {ranked
                        .filter((a) => a.value > 0)
                        .map((a) => (
                            <span
                                key={a.key}
                                title={`${a.label} ${Math.round((a.value / total) * 100)}% — ${a.desc}`}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '10px',
                                    color: PULSE_COLORS.textMuted,
                                    cursor: 'help',
                                }}
                            >
                                <span
                                    style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: a.color,
                                    }}
                                />
                                {a.label} {Math.round((a.value / total) * 100)}%
                            </span>
                        ))}
                </div>
            </div>

            <div
                style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: `1px solid ${PULSE_COLORS.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    fontSize: '11px',
                }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                    <span
                        title={`Strength — biggest share of effort: ${AREA_BY_KEY[strength.key].desc}`}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            borderRadius: '9999px',
                            background: 'rgba(16,185,129,0.18)',
                            color: '#34D399',
                            fontWeight: 500,
                            cursor: 'help',
                        }}
                    >
                        💪 {AREA_BY_KEY[strength.key].emoji} {strength.label}
                    </span>
                    {total > 0 && growth.key !== strength.key && (
                        <span
                            title={`Growth area — smallest share of effort: ${AREA_BY_KEY[growth.key].desc}`}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                borderRadius: '9999px',
                                background: 'rgba(245,158,11,0.18)',
                                color: '#FBBF24',
                                fontWeight: 500,
                                cursor: 'help',
                            }}
                        >
                            📈 grow {AREA_BY_KEY[growth.key].emoji} {growth.label}
                        </span>
                    )}
                </div>
                <div data-export-ignore="true">
                    <QaCoachButtons member={m} team={team} />
                </div>
            </div>
        </div>
    )
}
